import {
  AnalyticsActionSubscriptionCrossgradeEvent,
  AnalyticsActionSubscriptionDowngradeEvent,
  AnalyticsActionSubscriptionUpgradeEvent,
  AnalyticsEventParam,
  ChangeUserProduct,
  PurchaseDirection,
} from '@accedo/one-plugin-sdk';
import { useCallback } from 'react';

import { Plan } from '#/client/models';
import { durationToIso8601 } from '#/plugins/analytics/analyticsUtils';
import { PaymentStore } from '#/plugins/paywall/models';
import { falsyToNull } from '#/service/utils';
import { usePaywallStore } from '#/store/paywall/paywall.store';

import { NextSubscriptionDetails } from './PlanPurchaseFlow.types';

export const unproxy = <T>(value: T | undefined): T | undefined =>
  value ? (JSON.parse(JSON.stringify(value)) as T) : undefined;

export const useSendSubscriptionChangeEvent = () => {
  const { paywallApi } = usePaywallStore();

  const sendSubscriptionChangeEvent = useCallback(
    async (
      changeUserProductResponse: ChangeUserProduct,
      nextSubscription: NextSubscriptionDetails,
      planFrom: Plan | undefined,
      planTo: Plan | undefined
    ) => {
      const { name: providerName } = await paywallApi.getProviderInfo();

      const { transactionId, amountCharged, currency } =
        changeUserProductResponse;
      const { direction, duration } = nextSubscription;

      const payload = {
        [AnalyticsEventParam.PlanTo]: falsyToNull(unproxy(planTo)),
        [AnalyticsEventParam.PlanFrom]: falsyToNull(unproxy(planFrom)),
        [AnalyticsEventParam.Store]: providerName ?? PaymentStore.Unknown,
        [AnalyticsEventParam.TransactionId]: falsyToNull(transactionId),
        [AnalyticsEventParam.Price]: falsyToNull(amountCharged),
        [AnalyticsEventParam.Currency]: falsyToNull(currency),
        [AnalyticsEventParam.SubscriptionPeriod]: durationToIso8601(duration),
      };

      if (direction === PurchaseDirection.Up) {
        document.dispatchEvent(
          new AnalyticsActionSubscriptionUpgradeEvent(payload)
        );
      } else if (direction === PurchaseDirection.Down) {
        document.dispatchEvent(
          new AnalyticsActionSubscriptionDowngradeEvent(payload)
        );
      } else if (direction === PurchaseDirection.Cross) {
        document.dispatchEvent(
          new AnalyticsActionSubscriptionCrossgradeEvent(payload)
        );
      }
    },
    [paywallApi]
  );

  return { sendSubscriptionChangeEvent };
};
